import { Fragment, memo, useCallback, useMemo, useState } from 'react'

import { Slide, Link as MuiLink, ButtonBase } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'

import { useAnalytics } from '@/core/analytics'
import { Box } from '@/components/Layout'
import { Text } from '@/components/Data'
import {
  ChevronLeftThin,
  ChevronRightThick,
  citiesNavigationData,
  musicNavigationData,
  sportsNavigationData,
  ticketsNavigationData,
} from '@/assets'
import { globalClasses } from '@/styles'

import { Item, NavigationMenuData } from '../../../NavigationMenu/NavigationMenu.types'
import { NAVIGATION_LINK_NAME } from '../../../NavigationMenu/NavigationMenu.data'
import { Partners } from '../Partners'

import { navigationDrawerClasses } from '..'

import {
  childrenItemListStyles as childrenStyles,
  itemListStyles as styles,
} from './ItemList.styles'

interface ItemListProps {
  selectedNavItem: string
  closeDrawer: (currentState: boolean) => void
}

const ItemList = ({ selectedNavItem, closeDrawer }: ItemListProps) => {
  const [selectedItem, setSelectedItem] = useState<null | Item>()

  const selectedNavItemData = useMemo(() => {
    if (selectedNavItem === NAVIGATION_LINK_NAME.SPORTS) return sportsNavigationData
    else if (selectedNavItem === NAVIGATION_LINK_NAME.MUSIC) return musicNavigationData
    else if (selectedNavItem === NAVIGATION_LINK_NAME.TICKETS) return ticketsNavigationData
    else if (selectedNavItem === NAVIGATION_LINK_NAME.CITIES) return citiesNavigationData
  }, [selectedNavItem]) as NavigationMenuData[]

  const items = Array.isArray(selectedNavItemData) ? selectedNavItemData[0]?.items : []
  const isCitySelected = selectedNavItem === NAVIGATION_LINK_NAME.CITIES
  const onItemSelection = useCallback((currentItem: Item | null) => {
    setSelectedItem(currentItem)
  }, [])

  if (!selectedNavItem) return null

  if (selectedNavItem === NAVIGATION_LINK_NAME.PARTNERS)
    return <Partners closeDrawer={closeDrawer} />

  return (
    <Box sx={styles.container}>
      {items.map((item) => (
        <Fragment key={item.name}>
          {item.name !== selectedItem?.name && (
            <>
              {isCitySelected ? (
                <RouterLink to={item.link}>
                  <Box sx={styles.listItem}>
                    <Text sx={styles.itemName} className={globalClasses.ellipsis}>
                      {item.name}
                    </Text>
                  </Box>
                </RouterLink>
              ) : (
                <ButtonBase sx={styles.listItem} onClick={() => onItemSelection(item)}>
                  <Text sx={styles.itemName} className={globalClasses.ellipsis}>
                    {item.name}
                  </Text>
                  <ChevronRightThick className={navigationDrawerClasses.chevronRightIcon} />
                </ButtonBase>
              )}
            </>
          )}

          <ChildrenItemList
            selectedItem={selectedItem}
            onItemSelection={onItemSelection}
            currentItem={item}
            closeDrawer={closeDrawer}
          />
        </Fragment>
      ))}
    </Box>
  )
}

export default memo(ItemList)

interface ChildrenItemListProps {
  selectedItem?: Item | null
  currentItem: Item
  onItemSelection: (currentPerformer: Item | null) => void
  closeDrawer: (currentState: boolean) => void
}

const ChildrenItemList = ({
  selectedItem,
  currentItem,
  onItemSelection,
  closeDrawer,
}: ChildrenItemListProps) => {
  const analytics = useAnalytics()

  const onLinkClick = useCallback(
    (navText: string, navButton: string) => () => {
      closeDrawer(false)
      analytics.sendNavigationEvent(navText, `${navText}.${navButton}`)
    },
    [analytics, closeDrawer],
  )

  if (selectedItem?.name !== currentItem.name) return null

  return (
    <Slide direction="left" in mountOnEnter unmountOnExit>
      <Box sx={childrenStyles.container}>
        <Box sx={childrenStyles.header} onClick={() => onItemSelection(null)}>
          <ChevronLeftThin className={navigationDrawerClasses.chevronLeftIcon} />
          <Text sx={childrenStyles.selectedItemName}>{selectedItem?.name}</Text>
        </Box>

        {selectedItem?.children.map((childItem) => (
          <MuiLink
            component={RouterLink}
            onClick={onLinkClick(selectedItem.name, childItem.text)}
            to={childItem.link}
            key={childItem.text}
            sx={childrenStyles.childrenItem}>
            {childItem.primaryColor && (
              <Box sx={{ ...childrenStyles.colorBar, backgroundColor: childItem.primaryColor }} />
            )}
            <Text sx={childrenStyles.childrenItemName} className="ellipsis">
              {childItem.text}
            </Text>
          </MuiLink>
        ))}
      </Box>
    </Slide>
  )
}
